import React, {useEffect} from 'react';

import {Log} from '../../components/Log';
import {useAutoRefreshSettings} from '../../containers/AutoRefreshSettings';
import {useRunPageLogsQuery} from '../../generated/graphql';
import {useBasePollInterval} from '../../pages/Run/useBasePollInterval';
import {trackPageViewed} from '../../util/navigation';
import * as RunHelpers from '../../util/runhelpers';
import AnonymousClaimBanner from '../AnonymousClaimBanner';
import NoMatch from '../NoMatch';
import {TabWaveLoader} from './TabWaveLoader';

type RunPageLogsProps = {
  entityName: string;
  projectName: string;
  runName: string;
};

export const RunPageLogs: React.FC<RunPageLogsProps> = React.memo(
  ({entityName, projectName, runName}) => {
    const {data: runData, loading: runDataLoading} = useRunPageLogsQuery({
      variables: {
        projectName,
        entityName,
        runName,
      },
    });

    const run = runData?.project?.run;

    // for page view tracking
    let originPageName = 'Run';
    if (run?.group != null) {
      originPageName = 'Group run';
    } else if (run?.sweep != null) {
      originPageName = 'Sweep run';
    }

    // for page view tracking
    useEffect(() => {
      if (run != null) {
        trackPageViewed(originPageName, 'Logs');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [run]);

    const {autoRefreshEnabled} = useAutoRefreshSettings();

    const basePollInterval = useBasePollInterval({
      runState: run?.state ?? undefined,
      runHistoryKeys: run?.historyKeys,
      autoRefreshEnabled,
    });

    const pollInterval = autoRefreshEnabled ? basePollInterval : 0;

    if (runDataLoading) {
      return <TabWaveLoader />;
    }

    if (!run) {
      return <NoMatch />;
    }

    const outputLogFile = RunHelpers.outputLogFile(run?.outputLogFile);
    return (
      <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <AnonymousClaimBanner
          entityName={entityName}
          projectName={projectName}
          run={runName}
          createdAt={run?.createdAt ?? ''}
        />
        <div style={{flex: '1 1 auto'}}>
          <Log
            entityName={entityName}
            projectName={projectName}
            runName={runName}
            pollInterval={pollInterval}
            totalLines={run?.logLineCount ?? 0}
            outputLogFile={outputLogFile}
            analyticsLocation="run-logs"
          />
        </div>
      </div>
    );
  }
);
RunPageLogs.displayName = 'RunPageLogs';
